import { useEffect, useMemo, useState } from 'react';
import PageShell from '@/components/feature/PageShell';
import PageHero from '@/components/feature/PageHero';
import CourseCard, { type CourseItem } from '@/components/feature/CourseCard';
import PurchaseQrModal from '@/components/feature/PurchaseQrModal';

interface Crumb {
  label: string;
  to?: string;
}

interface CourseCatalogProps {
  heroImage: string;
  eyebrow: string;
  title: string;
  description: string;
  breadcrumb: Crumb[];
  items: CourseItem[];
}

export default function CourseCatalog({
  heroImage,
  eyebrow,
  title,
  description,
  breadcrumb,
  items,
}: CourseCatalogProps) {
  const [selected, setSelected] = useState<CourseItem | null>(null);
  const [keyword, setKeyword] = useState('');
  const [category, setCategory] = useState('all');
  const [sort, setSort] = useState('default');
  const pageSize = 8;
  const [visibleCount, setVisibleCount] = useState(pageSize);

  const sortOptions = [
    { value: 'default', label: '默认排序' },
    { value: 'price-asc', label: '价格从低到高' },
    { value: 'price-desc', label: '价格从高到低' },
    { value: 'lessons-desc', label: '课时从多到少' },
    { value: 'lessons-asc', label: '课时从少到多' },
  ];

  const categories = useMemo(
    () => Array.from(new Set(items.map((item) => item.category))),
    [items],
  );
  const hasCategories = categories.length > 1;

  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    const result = items.filter((item) => {
      const matchCategory = category === 'all' || item.category === category;
      const matchKeyword =
        !kw ||
        item.title.toLowerCase().includes(kw) ||
        item.doctor.toLowerCase().includes(kw);
      return matchCategory && matchKeyword;
    });

    switch (sort) {
      case 'price-asc':
        return [...result].sort((a, b) => a.price - b.price);
      case 'price-desc':
        return [...result].sort((a, b) => b.price - a.price);
      case 'lessons-desc':
        return [...result].sort((a, b) => b.lessons - a.lessons);
      case 'lessons-asc':
        return [...result].sort((a, b) => a.lessons - b.lessons);
      default:
        return result;
    }
  }, [items, keyword, category, sort]);

  const resetFilters = () => {
    setKeyword('');
    setCategory('all');
    setSort('default');
  };

  useEffect(() => {
    setVisibleCount(pageSize);
  }, [keyword, category, sort]);

  const visibleCourses = filtered.slice(0, visibleCount);
  const hasMore = filtered.length > visibleCount;
  const remaining = filtered.length - visibleCount;

  return (
    <PageShell>
      <PageHero
        image={heroImage}
        eyebrow={eyebrow}
        title={title}
        description={description}
        breadcrumb={breadcrumb}
      />

      <section className="w-full px-4 md:px-8 py-10 md:py-14">
        <div className="max-w-7xl mx-auto">
          {/* 筛选栏 */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="relative w-full sm:w-72">
                <i className="ri-search-2-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-base"></i>
                <input
                  type="text"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="搜索课程名称或讲师"
                  className="w-full pl-9 pr-9 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
                />
                {keyword.trim() && (
                  <button
                    type="button"
                    onClick={() => setKeyword('')}
                    aria-label="清除搜索"
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center rounded-full text-foreground-400 hover:text-foreground-700 hover:bg-background-200/60 transition-colors cursor-pointer"
                  >
                    <i className="ri-close-line text-base"></i>
                  </button>
                )}
              </div>

              {hasCategories && (
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setCategory('all')}
                    className={`px-3.5 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                      category === 'all'
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                    }`}
                  >
                    全部
                  </button>
                  {categories.map((cat) => (
                    <button
                      key={cat}
                      type="button"
                      onClick={() => setCategory(cat)}
                      className={`px-3.5 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                        category === cat
                          ? 'bg-primary-500 text-background-50'
                          : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                      }`}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="relative">
                <i className="ri-sort-desc absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-base pointer-events-none"></i>
                <select
                  value={sort}
                  onChange={(e) => setSort(e.target.value)}
                  aria-label="课程排序方式"
                  className="appearance-none pl-9 pr-8 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors cursor-pointer"
                >
                  {sortOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <i className="ri-arrow-down-s-line absolute right-2.5 top-1/2 -translate-y-1/2 text-foreground-400 text-base pointer-events-none"></i>
              </div>
              <span className="text-sm text-foreground-500 whitespace-nowrap">
                共 <span className="font-semibold text-primary-600">{filtered.length}</span> 门课程
              </span>
            </div>
          </div>

          {filtered.length > 0 ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
                {visibleCourses.map((course) => (
                  <CourseCard key={course.id} course={course} onBuy={setSelected} />
                ))}
              </div>

              {hasMore && (
                <div className="flex flex-col items-center mt-10">
                  <button
                    type="button"
                    onClick={() => setVisibleCount((count) => count + pageSize)}
                    className="inline-flex items-center gap-2 px-6 py-3 text-sm font-medium rounded-md border border-background-300 bg-background-50 text-foreground-800 hover:border-primary-400 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-add-line text-base"></i>
                    加载更多课程
                  </button>
                  <span className="mt-3 text-xs text-foreground-500">
                    已展示 {visibleCount} / {filtered.length} 门，还有 {remaining} 门
                  </span>
                </div>
              )}

              {!hasMore && filtered.length > pageSize && (
                <div className="flex items-center justify-center gap-2 mt-10 text-xs text-foreground-400">
                  <i className="ri-checkbox-circle-line text-base"></i>
                  已经到底啦，全部 {filtered.length} 门课程都在这里了
                </div>
              )}
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 text-center rounded-lg border border-background-200 bg-background-50">
              <div className="w-14 h-14 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
                <i className="ri-search-eye-line text-2xl"></i>
              </div>
              <p className="text-foreground-800 font-medium">没有找到匹配的课程</p>
              <p className="mt-1.5 text-sm text-foreground-500">换个关键词或分类试试。</p>
              <button
                type="button"
                onClick={resetFilters}
                className="mt-5 inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-md bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-refresh-line"></i>
                重置筛选
              </button>
            </div>
          )}
        </div>
      </section>

      <PurchaseQrModal
        open={selected !== null}
        onClose={() => setSelected(null)}
        courseTitle={selected?.title ?? ''}
        price={selected?.price ?? 0}
      />
    </PageShell>
  );
}